import { mkConfig, ConfigOptions } from "export-to-csv";

export interface ExportColumn {
  label: string;
  propName: string;
  csvLabel?: string;
  formatForExport?: (value: any) => string;
}

const defaultFormatter = (value: any): string => {
  if (value === true) return "Yes";
  if (value === false) return "No";
  return value != null ? String(value) : "";
};

/**
 * Generates CSV export config and row formatter. Automatically applies a default
 * formatter for boolean values to all columns unless a custom formatter is provided.
 *
 * @param columns Column definitions
 * @param title CSV title
 * @param filename CSV filename
 * @param skipFormatProps Props to exclude from default formatting (optional)
 */
export function createCsvConfigAndFormatter(
  columns: ExportColumn[],
  title: string,
  filename: string
): {
  csvOptions: ConfigOptions;
  formatRows: (rows: Record<string, any>[]) => Record<string, string>[];
} {
  const enhancedColumns = columns.map((col) => ({
    ...col,
    formatForExport:
      col.formatForExport ??
      ((value: any) => {
        if (typeof value === "boolean") return defaultFormatter(value);
        return value != null ? String(value) : "";
      }),
  }));

  const csvOptions = mkConfig({
    filename,
    title,
    showTitle: false, // if enabled, it overlaps the title and first column header in cell A1
    useKeysAsHeaders: false,
    columnHeaders: enhancedColumns.map((col) => ({
      key: col.propName,
      displayLabel: col.csvLabel ?? col.label,
    })),
  });

  const formatRows = (rows: Record<string, any>[]) =>
    rows.map((row) => {
      const formatted: Record<string, string> = {};
      for (const col of enhancedColumns) {
        const raw = row[col.propName];
        formatted[col.propName] = col.formatForExport!(raw);
      }
      return formatted;
    });

  return { csvOptions, formatRows };
}
